import { useState, useContext, useEffect, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { SiteContext } from "@/App";
import { Loader2, Eye, EyeOff, Instagram, RefreshCw, Sparkles, Star, ImagePlus, Trash2 } from "lucide-react";

const POSTS_PER_PAGE = 5;

export default function PostsTab({ token }) {
  const { API } = useContext(SiteContext);
  const [posts, setPosts] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [page, setPage] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [starring, setStarring] = useState(null);
  const [uploading, setUploading] = useState(false);
  const galleryInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchPosts = () => {
    axios.get(`${API}/posts/all`, { headers }).then(r => setPosts(r.data)).catch(() => {});
  };

  const fetchGallery = () => {
    axios.get(`${API}/gallery`).then(r => setGalleryImages(r.data || [])).catch(() => {});
  };

  const fetchSyncStatus = () => {
    axios.get(`${API}/analytics/stats`, { headers })
      .then(r => setSyncStatus({
        last: r.data.instagram_last_sync,
        message: r.data.instagram_sync_status,
        ok: r.data.instagram_sync_ok,
      })).catch(() => {});
  };

  useEffect(() => { fetchPosts(); fetchGallery(); fetchSyncStatus(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/instagram/sync`, {}, { headers });
      toast.success(data.message || `Sync complete — ${data.new_posts} new post(s)`);
      fetchPosts();
      fetchSyncStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleHidden = async (postId) => {
    setToggling(postId);
    try {
      const { data } = await axios.patch(`${API}/posts/${postId}/toggle-hidden`, {}, { headers });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, hidden: data.hidden } : p));
      toast.success(data.hidden ? "Post hidden from gallery" : "Post visible in gallery");
    } catch {
      toast.error("Failed to update post.");
    } finally {
      setToggling(null);
    }
  };

  const handleToggleStarred = async (postId) => {
    setStarring(postId);
    try {
      const { data } = await axios.patch(`${API}/posts/${postId}/toggle-starred`, {}, { headers });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, starred: data.starred } : p));
      toast.success(data.starred ? "Post featured" : "Post unstarred");
    } catch {
      toast.error("Failed to update post.");
    } finally {
      setStarring(null);
    }
  };

  const handleCategorize = async () => {
    setCategorizing(true);
    try {
      const { data } = await axios.post(`${API}/posts/categorize`, {}, { headers });
      toast.success(`Categorized ${data.categorized} of ${data.total} posts`);
      fetchPosts();
    } catch {
      toast.error("Categorization failed.");
    } finally {
      setCategorizing(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      const formData = new FormData();
      formData.append("file", file);
      formData.append("caption", "");
      try {
        await axios.post(`${API}/gallery`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
        uploaded++;
      } catch { /* skip */ }
    }
    toast.success(`${uploaded} image(s) added to headliner gallery`);
    fetchGallery();
    setUploading(false);
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  };

  const handleDeleteGalleryImage = async (imageId) => {
    try {
      await axios.delete(`${API}/gallery/${imageId}`, { headers });
      setGalleryImages(prev => prev.filter(i => i.id !== imageId));
      toast.success("Image removed from headliner.");
    } catch {
      toast.error("Failed to remove image.");
    }
  };

  const totalPages = Math.ceil(posts.length / POSTS_PER_PAGE);
  const visiblePosts = posts.slice(page * POSTS_PER_PAGE, (page + 1) * POSTS_PER_PAGE);
  const hiddenCount = posts.filter(p => p.hidden).length;
  const starredCount = posts.filter(p => p.starred).length;

  return (
    <div className="space-y-5 py-4">
      {/* Gallery Headliner Manager */}
      <div className="bg-ink-800 border border-amber-400/20 rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-manrope text-[10px] tracking-widest uppercase text-amber-400 mb-1 flex items-center gap-2">
              <Star size={12} className="fill-amber-400" />
              Gallery Headliner
            </p>
            <p className="font-manrope text-[10px] text-[#525252]">Pinned images at the top of the Work section — showcase trending tattoos</p>
          </div>
          <div>
            <input ref={galleryInputRef} type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="hidden" />
            <button
              data-testid="gallery-upload-btn"
              onClick={() => galleryInputRef.current?.click()}
              disabled={uploading}
              className="btn-verde py-2 px-4 flex items-center gap-2 text-[10px] disabled:opacity-60"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <ImagePlus size={12} />}
              {uploading ? "Uploading..." : "Add Images"}
            </button>
          </div>
        </div>

        {galleryImages.length > 0 ? (
          <div className="grid grid-cols-5 gap-2 mt-3">
            {galleryImages.map(img => (
              <div key={img.id} className="relative aspect-square rounded-sm overflow-hidden border border-ink-600 group">
                <img src={img.url} alt={img.caption || "Headliner"} className="w-full h-full object-cover" />
                <button
                  data-testid={`delete-gallery-${img.id}`}
                  onClick={() => handleDeleteGalleryImage(img.id)}
                  className="absolute top-1 right-1 bg-black/70 hover:bg-red-500/80 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 size={10} className="text-white" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="font-manrope text-[10px] text-[#404040] mt-2">No headliner images yet — add photos to pin them at the top of the gallery.</p>
        )}
      </div>

      {/* Sync Status */}
      <div className="bg-ink-800 border border-ink-600 rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-manrope text-[10px] tracking-widest uppercase text-verde mb-1">Daily Auto-Harvest</p>
            <p className="font-manrope text-[10px] text-[#525252]">Runs automatically every day at 3:00 AM UTC</p>
          </div>
          <button
            data-testid="instagram-sync-btn"
            onClick={handleSync}
            disabled={syncing}
            className="btn-verde py-2 px-4 flex items-center gap-2 text-[10px] disabled:opacity-60"
          >
            {syncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {syncing ? "Syncing..." : "Sync Now"}
          </button>
        </div>
        {syncStatus && (
          <div className="flex items-start gap-2 mt-2 pt-2 border-t border-ink-600">
            <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${syncStatus.ok === false ? "bg-red-400" : "bg-verde"}`} />
            <div>
              <p className={`font-manrope text-[10px] ${syncStatus.ok === false ? "text-red-400" : "text-verde"}`}>
                {syncStatus.message || "No sync run yet"}
              </p>
              {syncStatus.last && (
                <p className="font-manrope text-[9px] text-[#525252] mt-0.5">
                  Last: {new Date(syncStatus.last).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* AI Categorization */}
      <div className="bg-ink-800 border border-ink-600 rounded-sm p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-manrope text-[10px] tracking-widest uppercase text-verde mb-1">AI Categorization</p>
            <p className="font-manrope text-[10px] text-[#525252]">Auto-tag posts by style for gallery filtering</p>
          </div>
          <button
            data-testid="categorize-posts-btn"
            onClick={handleCategorize}
            disabled={categorizing}
            className="btn-verde py-2 px-4 flex items-center gap-2 text-[10px] disabled:opacity-60"
          >
            {categorizing ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {categorizing ? "Categorizing..." : "Categorize"}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between">
        <p className="font-manrope text-xs text-[#A3A3A3]">
          {posts.length} total post{posts.length !== 1 ? "s" : ""}
          {hiddenCount > 0 && <span className="text-red-400 ml-2">({hiddenCount} hidden)</span>}
          {starredCount > 0 && <span className="text-amber-400 ml-2">({starredCount} starred)</span>}
        </p>
        <p className="font-manrope text-[10px] text-[#525252]">
          Page {page + 1} of {totalPages || 1}
        </p>
      </div>

      {/* Posts List */}
      <div className="space-y-3">
        {visiblePosts.map(post => (
          <div
            key={post.id}
            data-testid={`admin-post-${post.id}`}
            className={`flex items-start gap-4 bg-ink-800 border rounded-sm p-3 transition-opacity ${post.hidden ? "border-red-500/30 opacity-60" : post.starred ? "border-amber-400/30" : "border-ink-600"}`}
          >
            <div className="w-20 h-20 flex-shrink-0 rounded-sm overflow-hidden bg-ink-900">
              {(post.media_url || post.thumbnail_url) ? (
                <img
                  src={post.media_url || post.thumbnail_url}
                  alt={post.title || "Post"}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Instagram size={20} className="text-ink-600" />
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-manrope text-sm text-[#E5E5E5] truncate">{post.title || "Untitled"}</p>
                {post.auto_harvested && (
                  <span className="font-manrope text-[8px] tracking-widest uppercase text-verde border border-verde/30 px-1.5 py-0.5 rounded flex-shrink-0">Auto</span>
                )}
                {post.starred && (
                  <span className="font-manrope text-[8px] tracking-widest uppercase text-amber-400 border border-amber-400/30 px-1.5 py-0.5 rounded flex-shrink-0">Featured</span>
                )}
                {post.hidden && (
                  <span className="font-manrope text-[8px] tracking-widest uppercase text-red-400 border border-red-400/30 px-1.5 py-0.5 rounded flex-shrink-0">Hidden</span>
                )}
              </div>
              <p className="font-manrope text-[10px] text-[#525252] truncate mb-1">{post.description?.slice(0, 80) || "No caption"}</p>
              <p className="font-manrope text-[9px] text-[#404040]">
                {post.category || "Work"} &middot; {post.media_type || "IMAGE"} &middot; {post.created_at ? new Date(post.created_at).toLocaleDateString() : "—"}
              </p>
            </div>

            <div className="flex flex-col gap-2 flex-shrink-0">
              <button
                data-testid={`toggle-starred-${post.id}`}
                onClick={() => handleToggleStarred(post.id)}
                disabled={starring === post.id}
                title={post.starred ? "Remove from featured" : "Feature this post"}
                className={`p-2 rounded-sm border transition-colors ${post.starred
                  ? "border-amber-400/30 text-amber-400 bg-amber-400/10 hover:bg-amber-400/20"
                  : "border-ink-600 text-[#525252] hover:text-amber-400 hover:border-amber-400/30"}`}
              >
                {starring === post.id ? <Loader2 size={14} className="animate-spin" /> : <Star size={14} className={post.starred ? "fill-amber-400" : ""} />}
              </button>
              <button
                data-testid={`toggle-hidden-${post.id}`}
                onClick={() => handleToggleHidden(post.id)}
                disabled={toggling === post.id}
                title={post.hidden ? "Show in gallery" : "Hide from gallery"}
                className={`p-2 rounded-sm border transition-colors ${post.hidden
                  ? "border-verde/30 text-verde hover:bg-verde/10"
                  : "border-ink-600 text-[#A3A3A3] hover:text-red-400 hover:border-red-400/30"}`}
              >
                {toggling === post.id ? <Loader2 size={14} className="animate-spin" /> : post.hidden ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
            </div>
          </div>
        ))}
        {posts.length === 0 && (
          <p className="font-manrope text-[#525252] text-xs text-center py-8">No posts yet — click Sync Now to harvest from Instagram.</p>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="font-manrope text-xs text-[#A3A3A3] hover:text-verde disabled:text-[#404040] disabled:cursor-not-allowed px-3 py-1.5 border border-ink-600 rounded-sm hover:border-verde disabled:hover:border-ink-600 transition-colors"
          >
            Prev
          </button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            const startPage = Math.max(0, Math.min(page - 2, totalPages - 5));
            const pageNum = startPage + i;
            if (pageNum >= totalPages) return null;
            return (
              <button
                key={pageNum}
                onClick={() => setPage(pageNum)}
                className={`font-manrope text-xs px-3 py-1.5 border rounded-sm transition-colors ${pageNum === page
                  ? "bg-verde/20 border-verde text-verde"
                  : "border-ink-600 text-[#525252] hover:text-[#A3A3A3] hover:border-ink-500"}`}
              >
                {pageNum + 1}
              </button>
            );
          })}
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="font-manrope text-xs text-[#A3A3A3] hover:text-verde disabled:text-[#404040] disabled:cursor-not-allowed px-3 py-1.5 border border-ink-600 rounded-sm hover:border-verde disabled:hover:border-ink-600 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
